import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FirestoreStorageService, SignupSessionData } from './firestore-storage.service';

/**
 * Every SignupSessionData field the signup form manages. Keys equal both the
 * legacy LocalStorageKey values and the Firestore field names, which is what
 * lets SessionStorageService.setItem route writes here transparently.
 */
export const SESSION_DOC_FIELDS: (keyof SignupSessionData)[] = [
  'userEmail',
  'userLocale',
  'agreeTerms',
  'venueName',
  'venueCategoryType',
  'venueType',
  'selectedPlan',
  'billingFrequency',
  'contactPerson',
  'contactPhoneNumber',
  'contactEmail',
  'streetAddress',
  'city',
  'stateProvince',
  'postalCode',
  'country',
  'internetAvailability',
  'nonprofitStatus',
  'monthlyOrderVolume',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmTerm',
  'utmContent'
];

const FLUSH_DELAY_MS = 1000;

/**
 * Single source of truth for the signup form's data.
 *
 * The Firestore session doc (signupSessions/{anonUid}) IS the store: the
 * SDK's IndexedDB persistence gives local-first reads/writes and offline
 * queueing, and onSnapshot keeps `state$` current (including latency-
 * compensated local writes). `patch()` applies changes optimistically and
 * debounces the actual Firestore write so a form-fill burst becomes one
 * merge write instead of one write per field.
 */
@Injectable({
  providedIn: 'root'
})
export class SignupStateService {
  private remoteState: SignupSessionData = {};
  private pending: Partial<SignupSessionData> = {};
  private stateSubject = new BehaviorSubject<SignupSessionData>({});
  readonly state$: Observable<SignupSessionData> = this.stateSubject.asObservable();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private stopSnapshots: (() => void) | null = null;

  constructor(private firestoreStorage: FirestoreStorageService) {
    // Best-effort: push any debounced-but-unwritten changes before the page
    // goes away. The SDK's offline queue covers the cases this misses.
    window.addEventListener('pagehide', () => {
      void this.flush();
    });
  }

  /**
   * Start (or restart) the session-doc subscription. Call after anonymous
   * auth is ready. Resolves once the first snapshot has been applied, then
   * migrates any venue data a pre-refactor client left in localStorage.
   */
  async initialize(): Promise<void> {
    this.stopSnapshots?.();

    await new Promise<void>((resolve) => {
      let resolved = false;
      this.stopSnapshots = this.firestoreStorage.subscribeToSession((data) => {
        this.remoteState = data || {};
        this.emit();
        if (!resolved) {
          resolved = true;
          resolve();
        }
      });
    });

    this.migrateLegacyLocalStorage();
  }

  /** Current merged state (remote doc + optimistic pending patches). */
  snapshot(): SignupSessionData {
    return this.stateSubject.value;
  }

  /**
   * Apply changes: optimistic locally right away, written to Firestore as a
   * single merge write after a short debounce.
   */
  patch(fields: Partial<SignupSessionData>): void {
    const clean: Partial<SignupSessionData> = {};
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) {
        (clean as Record<string, unknown>)[key] = value;
      }
    }
    if (Object.keys(clean).length === 0) {
      return;
    }

    this.pending = { ...this.pending, ...clean };
    this.emit();
    this.scheduleFlush();
  }

  /** Write any pending changes now (one merge write). */
  async flush(): Promise<boolean> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    const toWrite = this.pending;
    if (Object.keys(toWrite).length === 0) {
      return true;
    }
    this.pending = {};

    const ok = await this.firestoreStorage.setFields(toWrite);
    if (!ok) {
      // Put the failed fields back (under anything newer) so the next
      // patch/flush retries them instead of silently dropping data.
      this.pending = { ...toWrite, ...this.pending };
    }
    this.emit();
    return ok;
  }

  /**
   * Delete the session doc (end of signup / user reset). Cancels pending
   * writes first so the debounce can't resurrect the doc afterwards.
   */
  async clearSession(): Promise<boolean> {
    this.cancelPending();
    const ok = await this.firestoreStorage.deleteSession();
    this.remoteState = {};
    this.emit();
    return ok;
  }

  /** Drop local state without touching Firestore (e.g. after sign-out). */
  resetLocal(): void {
    this.cancelPending();
    this.remoteState = {};
    this.emit();
  }

  private cancelPending(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    this.pending = {};
  }

  private scheduleFlush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, FLUSH_DELAY_MS);
  }

  private emit(): void {
    this.stateSubject.next({ ...this.remoteState, ...this.pending });
  }

  /**
   * Carry-over for sessions started before the Firestore-backed store:
   * venue data still sitting in localStorage (and not yet in the doc) is
   * patched up, after which the doc is authoritative. Values are copied,
   * not removed — AttributionService still reads UTM keys from localStorage
   * for its first-touch check.
   */
  private migrateLegacyLocalStorage(): void {
    const migration: Partial<SignupSessionData> = {};
    for (const field of SESSION_DOC_FIELDS) {
      if (this.remoteState[field] !== undefined) {
        continue;
      }
      const raw = localStorage.getItem(field);
      if (raw === null || raw === '') {
        continue;
      }
      (migration as Record<string, unknown>)[field] = this.parseStoredValue(raw);
    }
    if (Object.keys(migration).length > 0) {
      console.log('Migrating legacy localStorage signup data:', Object.keys(migration));
      this.patch(migration);
    }
  }

  private parseStoredValue(raw: string): unknown {
    const trimmed = raw.trim();
    if (
      trimmed.startsWith('{') || trimmed.startsWith('[') ||
      trimmed === 'true' || trimmed === 'false' || trimmed === 'null' ||
      (trimmed !== '' && !isNaN(Number(trimmed)))
    ) {
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    }
    return raw;
  }
}
